import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ShieldAlert,
  AlertTriangle,
  Clock,
  User,
  Key,
  Filter,
  RefreshCw,
  Trash2,
  TrendingUp,
  Calendar,
} from 'lucide-react';
import { format, subDays } from 'date-fns';

export default function AuditLogsPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearDate, setClearDate] = useState('');
  
  // Filters
  const [filterUserId, setFilterUserId] = useState('');
  const [filterPermission, setFilterPermission] = useState('');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logsRes, summaryRes] = await Promise.all([
        api.get('/audit-logs', {
          params: {
            start_date: startDate,
            end_date: endDate,
            ...(filterUserId && { user_id: filterUserId }),
            ...(filterPermission && { permission: filterPermission }),
          }
        }),
        api.get('/audit-logs/summary', {
          params: { start_date: startDate, end_date: endDate }
        })
      ]);
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async () => {
    if (!clearDate) {
      toast.error('Please select a date');
      return;
    }

    try {
      const response = await api.delete('/audit-logs/clear', {
        params: { before_date: clearDate }
      });
      toast.success(response.data.message);
      setShowClearDialog(false);
      setClearDate('');
      loadData();
    } catch (error) {
      toast.error('Failed to clear audit logs');
    }
  };

  const getPermissionBadgeColor = (permission) => {
    const colors = {
      screens: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      campaigns: 'bg-green-500/10 text-green-400 border-green-500/20',
      content: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      analytics: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      billing: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      users: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    };
    const key = Object.keys(colors).find(k => permission.includes(k));
    return colors[key] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Security Audit Logs
          </h1>
          <p className="text-zinc-400 mt-1">
            Monitor unauthorized access attempts and permission violations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={loadData}
            className="border-zinc-700"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowClearDialog(true)}
            className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10"
            data-testid="clear-logs-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Old Logs
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-rose-500/10">
                  <ShieldAlert className="w-5 h-5 text-rose-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Total Violations</p>
                  <p className="text-white text-2xl font-bold">{summary.total_events}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Key className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Permissions Affected</p>
                  <p className="text-white text-2xl font-bold">
                    {Object.keys(summary.events_by_permission).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <User className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Users Flagged</p>
                  <p className="text-white text-2xl font-bold">
                    {Object.keys(summary.events_by_user).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Daily Average</p>
                  <p className="text-white text-2xl font-bold">
                    {summary.daily_trend.length > 0
                      ? Math.round(summary.total_events / summary.daily_trend.length)
                      : 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Breakdown Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Top Permissions */}
          <Card className="glass-card border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-400" />
                Top Blocked Permissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.entries(summary.events_by_permission).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(summary.events_by_permission).slice(0, 5).map(([perm, count]) => (
                    <div key={perm} className="flex items-center justify-between p-2 rounded-lg bg-zinc-800/50">
                      <Badge variant="outline" className={getPermissionBadgeColor(perm)}>
                        {perm}
                      </Badge>
                      <span className="text-white font-medium">{count} attempts</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-center py-4">No violations recorded</p>
              )}
            </CardContent>
          </Card>

          {/* Top Users */}
          <Card className="glass-card border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-400" />
                Users with Most Violations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.entries(summary.events_by_user).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(summary.events_by_user).slice(0, 5).map(([email, count]) => (
                    <div key={email} className="flex items-center justify-between p-2 rounded-lg bg-zinc-800/50">
                      <span className="text-zinc-300 text-sm truncate max-w-[200px]">{email}</span>
                      <Badge variant="outline" className="bg-rose-500/10 text-rose-400 border-rose-500/20">
                        {count} violations
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-center py-4">No violations recorded</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white w-40"
                data-testid="start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white w-40"
                data-testid="end-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Permission</Label>
              <Select value={filterPermission || "all"} onValueChange={(val) => setFilterPermission(val === "all" ? "" : val)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-40" data-testid="permission-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all" className="text-white">All Permissions</SelectItem>
                  <SelectItem value="screens" className="text-white">screens</SelectItem>
                  <SelectItem value="campaigns" className="text-white">campaigns</SelectItem>
                  <SelectItem value="content" className="text-white">content</SelectItem>
                  <SelectItem value="analytics" className="text-white">analytics</SelectItem>
                  <SelectItem value="billing" className="text-white">billing</SelectItem>
                  <SelectItem value="users" className="text-white">users</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={loadData}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="apply-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white">
            Audit Log Events ({logs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Timestamp</TableHead>
                  <TableHead className="text-zinc-500">User</TableHead>
                  <TableHead className="text-zinc-500">Role</TableHead>
                  <TableHead className="text-zinc-500">Permission Required</TableHead>
                  <TableHead className="text-zinc-500">User Permissions</TableHead>
                  <TableHead className="text-zinc-500">Severity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="border-zinc-800">
                    <TableCell className="text-zinc-400">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {format(new Date(log.timestamp), 'MMM d, HH:mm:ss')}
                      </div>
                    </TableCell>
                    <TableCell className="text-white">{log.user_email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20">
                        {log.user_role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getPermissionBadgeColor(log.required_permission)}>
                        {log.required_permission}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-400 text-sm max-w-[200px] truncate">
                      {log.user_permissions.length > 0 ? log.user_permissions.join(', ') : 'None'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                        {log.severity}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <ShieldAlert className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">No permission violations recorded</p>
              <p className="text-zinc-500 text-sm mt-1">
                When users attempt to access restricted resources, events will appear here
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clear Logs Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Clear Old Audit Logs
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-zinc-400">
              This will permanently delete all audit log entries before the selected date.
              This action cannot be undone.
            </p>
            <div className="space-y-2">
              <Label className="text-zinc-400">Delete logs before:</Label>
              <Input
                type="date"
                value={clearDate}
                onChange={(e) => setClearDate(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="clear-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowClearDialog(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleClearLogs}
              className="bg-rose-600 hover:bg-rose-500"
              data-testid="confirm-clear-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Logs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
